'use client';

import React, { useState, useEffect, useRef } from 'react';
import { routeAPI } from '@/services/api';
import { useRouter } from 'next/navigation';
import AdvancedDropdown from './AdvancedDropdown';

// CSS styles for popular routes section
const styles = `
  .hide-scrollbar {
    -ms-overflow-style: none;  /* IE and Edge */
    scrollbar-width: none;  /* Firefox */
  }
  .hide-scrollbar::-webkit-scrollbar {  /* Chrome, Safari, Opera */
    display: none;
  }
  
  @keyframes autoScroll {
    0% { transform: translateX(0); }
    100% { transform: translateX(-50%); }
  }
  
  .animate-autoscroll {
    display: inline-block;
    animation: autoScroll 30s linear infinite;
  }
  
  .animate-autoscroll:hover {
    animation-play-state: paused;
  }
`;

const HomepageBusSearch = () => {
  const router = useRouter();
  
  // State for dynamic date constraints (updated on client side only)
  const [dateConstraints, setDateConstraints] = useState({
    minDate: '',
    maxDate: ''
  });
  
  // Helper function to get local date string in YYYY-MM-DD format
  const getLocalDateString = () => {
    const now = new Date();
    const year = now.getFullYear();
    const month = String(now.getMonth() + 1).padStart(2, '0');
    const day = String(now.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };

  // Helper function to get date 7 days from now in YYYY-MM-DD format
  const getMaxDateString = () => {
    const now = new Date();
    now.setDate(now.getDate() + 7);
    const year = now.getFullYear();
    const month = String(now.getMonth() + 1).padStart(2, '0');
    const day = String(now.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };
  
  // Update date constraints on client side mount
  useEffect(() => {
    setDateConstraints({
      minDate: getLocalDateString(),
      maxDate: getMaxDateString()
    });
  }, []); // Run once on mount
  
  
  // Add styles to the document head
  useEffect(() => {
    const styleSheet = document.createElement('style');
    styleSheet.type = 'text/css';
    styleSheet.id = 'popular-routes-styles';
    styleSheet.innerHTML = styles;
    
    document.head.appendChild(styleSheet);
    
    // Cleanup function
    return () => {
      const existingStyle = document.getElementById('popular-routes-styles');
      if (existingStyle) {
        document.head.removeChild(existingStyle);
      }
    };
  }, []);
  const [searchData, setSearchData] = useState({
    origin: '',
    destination: '',
    travelDate: '',
  });
  const [routes, setRoutes] = useState([]);
  const [error, setError] = useState('');

  useEffect(() => {
    const fetchRoutes = async () => {
      try {
        const routeData = await routeAPI.getRoutes();
        setRoutes(routeData);
      } catch (err) {
        console.error('Failed to fetch routes:', err);
        setError('Failed to load routes');
      }
    };
    
    fetchRoutes();
    
    // Cleanup function
    return () => {
      // No specific cleanup needed for this component
    };
  }, []);

  // Prepare origins and destinations for AdvancedDropdown (simple strings)
  const getOrigins = () => {
    const origins = [...new Set(routes.map(route => route.origin))];
    return origins.sort();
  };

  const getDestinations = () => {
    if (!searchData.origin) return [];
    const destinations = routes
      .filter(route => route.origin === searchData.origin)
      .map(route => route.destination);
    return [...new Set(destinations)].sort();
  };

  const handleOriginSelect = (event) => {
    const selected = event.target.value;
    setSearchData(prev => ({ ...prev, origin: selected, destination: '' }));
  };

  const handleDestinationSelect = (event) => {
    const selected = event.target.value;
    setSearchData(prev => ({ ...prev, destination: selected }));
  };

  const handleSearch = (e) => {
    e.preventDefault();

    // Add this validation at the beginning of your function
    const today = new Date();
    const maxDate = new Date();
    maxDate.setDate(today.getDate() + 7);
    const selectedDate = new Date(searchData.travelDate);
    
    // Normalize dates to compare only date parts (without time)
    const todayDate = new Date(today.getFullYear(), today.getMonth(), today.getDate());
    const selectedDateNormalized = new Date(selectedDate.getFullYear(), selectedDate.getMonth(), selectedDate.getDate());
    const maxDateNormalized = new Date(maxDate.getFullYear(), maxDate.getMonth(), maxDate.getDate());
    
    if (selectedDateNormalized < todayDate || selectedDateNormalized > maxDateNormalized) {
      setError('Please select a date within the next 7 days');
      return;
    }

    // Validate required fields
    if (!searchData.origin || !searchData.destination || !searchData.travelDate) {
      setError('Please fill in all fields');
      return;
    }

    // Redirect to search page with query parameters
    const queryParams = new URLSearchParams({
      from: searchData.origin,
      to: searchData.destination,
      date: searchData.travelDate
    }).toString();

    router.push(`/search?${queryParams}`);
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setSearchData(prevData => ({
      ...prevData,
      [name]: value
    }));

    // Clear error when user starts typing
    if (error) {
      setError('');
    }
  };

  // Settings for the popular routes carousel
  const sliderSettings = {
    dots: false,
    infinite: true,
    speed: 500,
    slidesToShow: 5,
    slidesToScroll: 1,
    autoplay: true,
    autoplaySpeed: 2000,
    pauseOnHover: true,
    swipeToSlide: true,
    arrows: false,
    responsive: [
      {
        breakpoint: 1024,
        settings: {
          slidesToShow: 4,
        },
      },
      {
        breakpoint: 768,
        settings: {
          slidesToShow: 2,
        },
      },
      {
        breakpoint: 480,
        settings: {
          slidesToShow: 1,
        },
      },
    ],
  };

  return (
    <div className="bg-white p-4 sm:p-8 rounded-lg shadow-lg max-w-4xl mx-auto mt-8">
      <form onSubmit={handleSearch} className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-4 items-end">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">From</label>
          <AdvancedDropdown
            options={getOrigins()}
            value={searchData.origin}
            onChange={handleOriginSelect}
            placeholder="Select Origin"
            searchPlaceholder="Search origin..."
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">To</label>
          <AdvancedDropdown
            options={getDestinations()}
            value={searchData.destination}
            onChange={handleDestinationSelect}
            placeholder="Select Destination"
            searchPlaceholder="Search destination..."
            disabled={!searchData.origin}
          />
        </div>
        <div>
          <label htmlFor="travelDate" className="block text-sm font-medium text-gray-700">Date</label>
          <input
            type="date"
            id="travelDate"
            name="travelDate"
            value={searchData.travelDate}
            onChange={handleChange}
            min={dateConstraints.minDate}
            max={dateConstraints.maxDate}
            className="mt-1 block w-full pl-3 pr-3 py-2 base text-gray-900 border-gray-300 focus:outline-none focus:ring-[#16A34A] focus:border-[#16A34A] sm:text-sm rounded-md"
            required
          />
        </div>
        <button
          type="submit"
          className="w-full bg-[#16A34A] text-white py-2 px-4 rounded-md hover:bg-[#128a3a] transition-colors"
        >
          Find Tickets
        </button>
      </form>
      {error && <p className="text-red-500 text-sm mt-4">{error}</p>}
      {routes.length > 0 && (
        <div className="mt-6">
          <h3 className="text-lg font-medium text-gray-700 mb-2">Popular Routes</h3>
          <div className="pb-4 hide-scrollbar">
            <div className="relative w-full overflow-hidden h-10">
              <div className="animate-autoscroll whitespace-nowrap space-x-2">
                {[...routes.slice(0, 10), ...routes.slice(0, 10)].map((route, index) => (
                  <button
                    key={`autoscroll-${index}`}
                    type="button"
                    onClick={() => {
                      setSearchData(prev => ({
                        ...prev,
                        origin: route.origin,
                        destination: route.destination,
                        // If no travel date is set, default to today
                        travelDate: prev.travelDate || dateConstraints.minDate || getLocalDateString()
                      }));
                    }}
                    className="inline-block bg-gray-100 hover:bg-[#16A34A] hover:text-white text-gray-800 px-3 py-1 rounded-md text-sm transition-colors whitespace-nowrap h-10 align-top"
                  >
                    {route.origin} → {route.destination}
                  </button>
                ))}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default HomepageBusSearch;
